import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Loader2 } from "lucide-react";
import { Line, LineChart, ResponsiveContainer, YAxis } from "recharts";
import PriceChart from "@/components/PriceChart";
import { useApi } from "@/lib/api";
import { eur, num, pct, dt } from "@/lib/format";

const UNITS = {
  kupfer: [["€/kg", 1, 3], ["€/t", 1000, 0]],
  gold: [["€/g", 0.001, 2], ["€/oz", 0.0311034768, 2]],
  silber: [["€/kg", 1, 0], ["€/g", 0.001, 3]],
  aluminium: [["€/kg", 1, 3], ["€/t", 1000, 0]],
  stahl: [["€/t", 1000, 0], ["€/kg", 1, 3]],
};
const ACCENT = { kupfer: "#EA580C", gold: "#CA8A04", silber: "#64748B", aluminium: "#0EA5E9", stahl: "#475569" };

export const useMetals = () => {
  const api = useApi();
  const [metals, setMetals] = useState(null);
  useEffect(() => {
    api.get("/metals").then((r) => setMetals(r.data)).catch(() => setMetals(false));
  }, [api]);
  return metals;
};

const Spark = ({ data, color }) => (
  <ResponsiveContainer width="100%" height="100%">
    <LineChart data={data.map((v, i) => ({ i, v }))}>
      <YAxis hide domain={["auto", "auto"]} />
      <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
    </LineChart>
  </ResponsiveContainer>
);

export const MetalCard = ({ m, active, onClick, compact }) => {
  const [main, second] = UNITS[m.key];
  const up = m.change_pct >= 0;
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={`panel w-full p-4 text-left fade-up ${active ? "ring-2 ring-orange-400" : ""}`} data-testid={`metal-card-${m.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: ACCENT[m.key] }} />
          <span className="font-semibold">{m.name}</span>
        </div>
        <span className={`num inline-flex shrink-0 items-center whitespace-nowrap text-xs ${up ? "text-emerald-600" : "text-red-600"}`}><Arrow className="h-3.5 w-3.5" />{pct(m.change_pct)}</span>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="num text-2xl font-bold" data-testid={`metal-price-${m.key}`}>{eur(m.eur_per_kg * main[1], main[2])}</div>
          <div className="text-xs text-slate-500">{main[0]}{!compact && ` · ${eur(m.eur_per_kg * second[1], second[2])}${second[0].slice(1)}`}</div>
        </div>
        <div className="h-10 w-24"><Spark data={m.spark} color={ACCENT[m.key]} /></div>
      </div>
      {!compact && <div className="mt-2 text-[11px] text-slate-500">30 Tage: {pct(m.change_30d_pct)} · {m.source}</div>}
    </Tag>
  );
};

export const MetalsStrip = () => {
  const metals = useMetals();
  if (metals === false) return null;
  return (
    <div className="md:col-span-3 lg:col-span-4" data-testid="dashboard-metals-strip">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold">Weitere Metalle</h3>
        <Link to="/metalle" className="text-xs text-orange-700 hover:underline" data-testid="link-metals">Alle Metallkurse</Link>
      </div>
      {!metals ? <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-orange-600" /></div> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {metals.filter((m) => m.key !== "kupfer").map((m) => (
            <Link key={m.key} to={`/metalle?m=${m.key}`}><MetalCard m={m} compact /></Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Metalle() {
  const metals = useMetals();
  const initial = new URLSearchParams(window.location.search).get("m") || "gold";
  const [sel, setSel] = useState(initial);
  const chartRef = useRef(null);
  const pick = (key) => {
    setSel(key);
    if (window.innerWidth < 1280) chartRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  if (metals === false) return <p className="py-16 text-center text-sm text-slate-500">Metallkurse derzeit nicht verfügbar.</p>;
  if (!metals) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>;
  const m = metals.find((x) => x.key === sel) || metals[0];
  const [main] = UNITS[m.key];
  return (
    <div className="space-y-6" data-testid="metals-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Metallkurse</h1>
        <p className="mt-1 text-sm text-slate-500">Börsenkurse (COMEX-Futures) umgerechnet in Euro. Stand {dt(m.market_time)}, bis zu 15 Minuten verzögert.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {metals.map((x) => <MetalCard key={x.key} m={x} active={x.key === m.key} onClick={() => pick(x.key)} />)}
      </div>
      <div className="panel scroll-mt-20 p-5" ref={chartRef}>
        <h3 className="mb-2 text-base font-semibold" data-testid="metal-chart-title">Kursverlauf {m.name} ({main[0]})</h3>
        <PriceChart metal={m.key} factor={main[1]} digits={main[2]} unit={main[0]} />
      </div>
      <p className="text-xs text-slate-500">Stahl: US-Warmband (HRC, Midwest) als Richtwert für den Stahlmarkt – Schrottpreise für Stahl liegen deutlich darunter. Gold/Silber: Feinmetallpreis.</p>
    </div>
  );
}
